// GegenueberKasten.tsx — die einheitliche Optik der Rückfrage „mit wem?" im
// Status-Schritt der Kontaktanlage (User 02.10.2026).
//
// Jede Rolle, die ein Gegenüber braucht, bekommt denselben Kasten: oben die
// Frage („Verantwortung fürs Unternehmen — für welches Unternehmen?"), darunter
// SOFORT das Suchfeld — nicht erst nach einem Klick auf „+ Unternehmen". Über
// allen Kästen steht „Eine der gewählten Rollen braucht ein Gegenüber:",
// darunter der gelbe Hinweis, solange noch eines fehlt.
//
// Hier steckt nur die Optik und die Listenmechanik. WAS gespeichert wird,
// entscheiden die Aufrufer (`StatusBezuege` für die Rollen mit `bezug`,
// `relationsInline/StatusFolgefelder` für die mit `folge`) — unverändert.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";

const useStyles = makeStyles({
  block: {
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
    marginTop: "12px",
  },
  einleitung: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.86rem",
    color: REFINED.ink2,
    lineHeight: 1.45,
  },
  karte: {
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("12px", "12px", "14px"),
    backgroundColor: REFINED.white,
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  frage: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    fontWeight: 600,
    color: REFINED.ink,
  },
  offen: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    color: REFINED.goldInk,
  },
  eintrag: { display: "flex", flexDirection: "column", rowGap: "8px" },
  trenner: {
    height: "1px",
    backgroundColor: REFINED.line,
    ...shorthands.margin("6px", "0"),
    ...shorthands.borderWidth("0"),
  },
  fuss: { display: "flex", justifyContent: "space-between", alignItems: "center", columnGap: "12px" },
  // Textknöpfe wie „+ weitere Rolle".
  link: {
    appearance: "none",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    ...shorthands.padding("0"),
    ":hover": { textDecorationLine: "underline" },
  },
  weg: {
    appearance: "none",
    marginLeft: "auto",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.ink3,
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    ...shorthands.padding("0"),
    ":hover": { color: REFINED.err, textDecorationLine: "underline" },
  },
});

/** Der Satz über den Kästen. */
export const einleitungsText = (anzahl: number): string =>
  anzahl === 1
    ? "Eine der gewählten Rollen braucht ein Gegenüber:"
    : `${anzahl} der gewählten Rollen brauchen ein Gegenüber:`;

/** Der gelbe Hinweis unter den Kästen — `null`, wenn nichts offen ist. */
export const offenText = (offen: number): string | null =>
  offen <= 0
    ? null
    : offen === 1
      ? "Noch ein Gegenüber offen — die Rolle bleibt sonst ohne Bezug."
      : `Noch ${offen} Gegenüber offen — diese Rollen bleiben sonst ohne Bezug.`;

/**
 * Einleitung, Kästen, Hinweis. `anzahl` ist die Zahl der Kästen, `offen` die
 * der Kästen ohne Gegenüber. Ohne Kasten erscheint nichts.
 */
export function GegenueberRahmen({
  anzahl,
  offen,
  children,
}: {
  anzahl: number;
  offen: number;
  children: React.ReactNode;
}) {
  const s = useStyles();
  if (anzahl <= 0) return null;
  const hinweis = offenText(offen);
  return (
    <div className={s.block}>
      <span className={s.einleitung}>{einleitungsText(anzahl)}</span>
      {children}
      {hinweis && <span className={s.offen}>{hinweis}</span>}
    </div>
  );
}

/** Ein Kasten: die Frage und darunter die Felder. */
export function GegenueberKarte({ frage, children }: { frage: string; children: React.ReactNode }) {
  const s = useStyles();
  return (
    <div className={s.karte} role="group" aria-label={frage}>
      <span className={s.frage}>{frage}</span>
      {children}
    </div>
  );
}

/**
 * Mehrere Gegenüber in EINEM Kasten — für die Rollen, deren Bezüge als Liste
 * gespeichert werden (Unternehmen, Verein, Personen mit Beziehung).
 *
 * Ist die Liste leer, steht trotzdem schon ein Eintrag mit Suchfeld da: ein
 * Entwurf, der erst in die Liste wandert, sobald er Inhalt hat (`istLeer`).
 * So landet nie ein leerer Eintrag in den Beziehungskarten weiter unten. Der
 * Entwurf behält beim Übernehmen seine id — das Suchfeld bleibt dasselbe
 * Element und verliert weder Fokus noch Eintipp-Modus.
 */
export function GegenueberListe<T extends { id: string }>({
  items,
  onChange,
  neu,
  istLeer,
  renderFelder,
  weiteresLabel,
}: {
  items: T[];
  onChange: (next: T[]) => void;
  neu: () => T;
  istLeer: (item: T) => boolean;
  renderFelder: (item: T, update: (patch: Partial<T>) => void) => React.ReactNode;
  /** Ohne „+", z. B. „weiteres Unternehmen". */
  weiteresLabel: string;
}) {
  const s = useStyles();
  const [entwurf, setEntwurf] = React.useState<T>(neu);
  // „+ weiteres …" geklickt: der Entwurf steht zusätzlich unter der Liste.
  const [mehr, setMehr] = React.useState(false);
  const zeigeEntwurf = items.length === 0 || mehr;

  const entwurfAendern = (patch: Partial<T>) => {
    const next = { ...entwurf, ...patch };
    if (istLeer(next)) return setEntwurf(next);
    onChange([...items, next]);
    setEntwurf(neu());
    setMehr(false);
  };

  const eintragAendern = (id: string, patch: Partial<T>) => {
    const alt = items.find((it) => it.id === id);
    if (!alt) return;
    const next = { ...alt, ...patch };
    if (!istLeer(next)) return onChange(items.map((it) => (it.id === id ? next : it)));
    // Wieder leer: raus aus der Liste (sonst stünde unten „Ohne Namen"), aber
    // als Entwurf an derselben Stelle stehen lassen.
    onChange(items.filter((it) => it.id !== id));
    setEntwurf(next);
    setMehr(true);
  };

  const eintraege: Array<{ it: T; entwurf: boolean }> = [
    ...items.map((it) => ({ it, entwurf: false })),
    ...(zeigeEntwurf ? [{ it: entwurf, entwurf: true }] : []),
  ];

  return (
    <>
      {eintraege.map(({ it, entwurf: istEntwurf }, i) => (
        <div className={s.eintrag} key={it.id}>
          {i > 0 && <hr className={s.trenner} />}
          {renderFelder(it, (patch) => (istEntwurf ? entwurfAendern(patch) : eintragAendern(it.id, patch)))}
          {/* Entfernen nur, wo es etwas zu entfernen gibt: ein erfasster
              Eintrag oder ein zusätzlich geöffneter Entwurf. */}
          {(!istEntwurf || items.length > 0) && (
            <div className={s.fuss}>
              <button
                type="button"
                className={s.weg}
                onClick={() => {
                  if (istEntwurf) {
                    setEntwurf(neu());
                    setMehr(false);
                  } else onChange(items.filter((x) => x.id !== it.id));
                }}
              >
                Entfernen
              </button>
            </div>
          )}
        </div>
      ))}
      {!zeigeEntwurf && (
        <div className={s.fuss}>
          <button type="button" className={s.link} onClick={() => setMehr(true)}>
            + {weiteresLabel}
          </button>
        </div>
      )}
    </>
  );
}
